import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import CustomCard from '@qler-view/shared_lib/src/shared/lib/ui-components/Card/CustomCard';
import CustomCardContent from '@qler-view/shared_lib/src/shared/lib/ui-components/Card/CustomCardContent';
import CustomIconButton from '@qler-view/shared_lib/src/shared/lib/ui-components/IconButton/CustomIconButton';
import BackIcon from '@assets/images/BackIcon.svg';
import PreviousIcon from '@assets/images/PreviousIcon.svg';
import NextIcon from '@assets/images/NextIcon.svg';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CONSULTS_MORE_DETAILS_ROUTE_PATH,
  CONSULTS_ROUTE_PATH,
  NEW_CONSULTS_ROUTE_PATH,
} from '../../routers/routePaths';
import CustomTypography from '@qler-view/shared_lib/src/shared/lib/ui-components/Typography/CustomTypography';
import CustomDivider from '@qler-view/shared_lib/src/shared/lib/ui-components/Divider/CustomDivider';
import {
  formatFieldValue,
  isValidUrl,
} from '@qler-view/shared_lib/src/reusableFunctions/CommonFunctions';
import CustomLink from '@qler-view/shared_lib/src/shared/lib/ui-components/Link/CustomLink';
import CustomTabs from '@qler-view/shared_lib/src/shared/lib/ui-components/Tabs/CustomTabs';
import CustomTabPanel from '@qler-view/shared_lib/src/shared/lib/ui-components/Tabs/CustomTabPanel';
import ChatQLER from '@qler-view/shared_lib/src/shared/Chat/ChatQLER';
import {
  useGetAcsChatToken,
  useGetConsultChatThread,
} from '../../API/hooks/acsChatHook';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src/shared/constants/portalType';
import { getUserFromStorage } from '../login/storageUtility';
import {
  useOpsConsultMoreDetails,
  useUploadSessionDocument,
} from '../../API/hooks/ConsultMoreDetailsHook';
import { CallStatusEnum } from '../../shared/CallStatusEnum';
import CustomBadge from '@qler-view/shared_lib/src/shared/lib/ui-components/Badge/CustomBadge';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import ChatIcon from '@assets/images/chat.svg';
import { useConsultHistory } from '../../shared/History/consultHistorySetup';
import ConsultHistory from '@qler-view/shared_lib/src/shared/History/ConsultHistory';
import PastSessions from './PastSessions';
import { CustomDialog, CustomDialogContent } from '@qler-view/shared_lib/src';
import CustomDragZone from '@qler-view/shared_lib/src/shared/lib/ui-components/Dropzone/CustomDropzone';
import { type SuccessResponse } from '../../API/services/ConsultMoreDetailsService';
import SessionDoc from './SessionDoc';
import CustomSnackbar from '@qler-view/shared_lib/src/shared/lib/ui-components/Snackbar/CustomSnackbar';

const ConsultMoreDetailsOps = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUserFromStorage();
  const { consultId } = useParams<{ consultId?: string }>();
  const [activeTab, setActiveTab] = useState<number | string>(
    location.state?.cameFromBack ? 1 : 0
  );

  const [isChatOpen, setIsChatOpen] = useState(false);

  const { acsChatToken } = useGetAcsChatToken(
    user?.userId ?? 0,
    PORTAL_TYPES.OPERATIONS
  );
  const { consultChatThread } = useGetConsultChatThread(
    Number(consultId),
    user?.userId ?? 0,
    PORTAL_TYPES.OPERATIONS,
    isChatOpen
  );

  const { opsConsultMoreDetails, isLoading } = useOpsConsultMoreDetails(
    Number(consultId),
    user?.userId ?? 0
  );

  const { consultHistory, isLoading: historyLoading } = useConsultHistory(
    Number(consultId),
    PORTAL_TYPES.OPERATIONS
  );
  const userId = Number.parseInt(localStorage.getItem('userId') ?? '0');

  const [openFileUpload, setOpenFileUpload] = useState(false);
  const handleFileUploadOpen = () => setOpenFileUpload(true);
  const handleFileUploadClose = () => setOpenFileUpload(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  const refetch = () => {
    console.log('refetch');
  };

  const { mutate: mutateUploadSessionDocument, isPending } =
    useUploadSessionDocument(refetch);

  const tabItems = [
    { label: 'Facility Fields', value: 0 },
    {
      label:
        'Past Consults ' + '(' + opsConsultMoreDetails?.pastSessionCount + ')',
      value: 1,
    },
    {
      label:
        'Consult Documents ' +
        '(' +
        opsConsultMoreDetails?.sessionDocumentCount +
        ')',
      value: 2,
    },
    { label: 'History', value: 3 },
  ];

  const setViewOnly = (status?: string) => {
    const normalizedStatus = status?.toLowerCase();
    const isClosed =
      normalizedStatus === CallStatusEnum.Completed.toLowerCase() ||
      normalizedStatus === CallStatusEnum.Cancelled.toLowerCase();

    return isClosed;
  };

  const renderFieldValue = (fieldLabel: string, fieldValue: string) => {
    if (isValidUrl(fieldValue)) {
      const href =
        fieldValue?.startsWith('http://') || fieldValue?.startsWith('https://')
          ? fieldValue
          : `https://${fieldValue}`;
      return (
        <CustomLink
          id={`lbl-${fieldLabel}-value`}
          variant="body3"
          className="content-align"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          color="primary"
        >
          {fieldValue}
        </CustomLink>
      );
    }
    return (
      <CustomTypography
        id={`lbl-${fieldLabel}-value`}
        variant="body3"
        className="content-align"
      >
        {formatFieldValue(fieldValue)}
      </CustomTypography>
    );
  };

  const getAcsConfig = () => {
    if (!acsChatToken) {
      return undefined;
    }
    const displayName =
      `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ??
      user?.username ??
      'User';
    return {
      endpoint: import.meta.env.VITE_ACS_ENDPOINT,
      token: acsChatToken.token,
      userId: acsChatToken.acsUserId,
      displayName,
      portalType: PORTAL_TYPES.OPERATIONS,
    };
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number | string
  ) => {
    setActiveTab(newValue);
  };

  const handleChatToggle = () => {
    setIsChatOpen((prev) => !prev);
  };

  useEffect(() => {
    if (location.state?.chatOpen) {
      setIsChatOpen(true);
    }
  }, [location.state?.chatOpen]);

  const handleEditConsultInfo = (consultId: number) => {
    navigate(NEW_CONSULTS_ROUTE_PATH + `/${consultId}`, {
      replace: true,
      state: { consultId: consultId },
    });
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  const uploadDocument = (files: File[] | null) => {
    setErrorMessage(null);

    if (files && files.length > 0) {
      const validExtensions = ['.doc', '.docx', '.pdf'];

      const filteredFiles = files.filter((file) =>
        validExtensions.some((ext) =>
          file.name.trim().toLowerCase().endsWith(ext)
        )
      );
      const invalidFiles = files.filter(
        (file) =>
          !validExtensions.some((ext) =>
            file.name.trim().toLowerCase().endsWith(ext)
          )
      );

      if (invalidFiles.length > 0) {
        setErrorMessage(
          'Some files have invalid formats. Only Word or PDF files will be uploaded.'
        );
      } else if (filteredFiles.length > 0) {
        mutateUploadSessionDocument(
          {
            files: filteredFiles,
            providerId: userId,
            consultId: Number(consultId),
          },
          {
            onSuccess: (data: SuccessResponse) => {
              console.log('data', data);
              setOpenFileUpload(false);
              setMessage('Document uploaded successfully.');
              setOpenToast(true);
            },
            onError: (error) => {
              console.log('error', error);
              setOpenFileUpload(false);
            },
          }
        );
      } else {
        setErrorMessage(
          'No valid files selected. Please upload Word or PDF files only.'
        );
      }
    }
  };

  const getValueOrNA = (value: string | undefined | null) =>
    value?.trim() ? value : 'N/A';
  const getBoolenValue = (value: boolean | undefined) => (value ? 'Yes' : 'No');
  const getFirstChar = (value: string | undefined | null) => {
    if (!value) return '';
    const lowerValue = value.toLowerCase();
    if (lowerValue === 'operations') return 'OPS';
    if (lowerValue === 'facility') return 'FAC';
    if (lowerValue === 'clinical') return 'CLI';
    return value.charAt(0);
  };

  const handleViewConsults = (consultId: number) => {
    navigate(CONSULTS_MORE_DETAILS_ROUTE_PATH + `/${consultId}`);
  };

  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="toast-consult-list"
          successOrError={'success'}
          open={openToast}
          handleClose={() => {
            setOpenToast(!openToast);
          }}
          toastrMessage={message}
        />
      )}
      <Box>
        <Box className="pt-20 pb-20">
          <Grid2 container>
            <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
              <Box className="flex-justify-space-between align-items-baseline">
                <Box className="text-align-left display-flex">
                  <CustomIconButton
                    id="back-button"
                    icon={<BackIcon />}
                    onClick={() => {
                      navigate(CONSULTS_ROUTE_PATH);
                    }}
                  ></CustomIconButton>
                  <CustomTypography
                    className="pl-12"
                    variant="h4"
                    id="partner-business-id"
                  >
                    {consultId
                      ? `View Consult - (${opsConsultMoreDetails?.patientName})`
                      : 'View Consult'}
                  </CustomTypography>
                </Box>

                <Box className="flex align-items-center"></Box>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
              <Box className="flex display-flex justify-end">
                <Box className="mr-8 align-items-center">
                  <CustomIconButton
                    id="back-button"
                    icon={<PreviousIcon />}
                    onClick={() => {}}
                  ></CustomIconButton>
                  <CustomLink
                    id="previous-button"
                    onClick={() => {
                      if (opsConsultMoreDetails?.previousConsultId) {
                        handleViewConsults(
                          opsConsultMoreDetails.previousConsultId
                        );
                      } else {
                        navigate(CONSULTS_ROUTE_PATH);
                      }
                    }}
                    underline="none"
                    disabled={!opsConsultMoreDetails?.previousConsultId}
                  >
                    PREVIOUS
                  </CustomLink>
                </Box>
                {/* <Box>-</Box> */}
                <Box className="ml-8 align-items-center">
                  <CustomLink
                    id="next-button"
                    onClick={() => {
                      if (opsConsultMoreDetails?.nextConsultId) {
                        handleViewConsults(opsConsultMoreDetails.nextConsultId);
                      } else {
                        navigate(CONSULTS_ROUTE_PATH);
                      }
                    }}
                    underline="none"
                    disabled={!opsConsultMoreDetails?.nextConsultId}
                  >
                    NEXT
                  </CustomLink>
                  <CustomIconButton
                    id="next-button"
                    icon={<NextIcon />}
                    onClick={() => {}}
                  ></CustomIconButton>
                </Box>
              </Box>
            </Grid2>
          </Grid2>
        </Box>
        <Box className="flex">
          <Grid2 container spacing={3} className="width-100">
            <Grid2
              size={{
                xs: 12,
                sm: 12,
                md: isChatOpen ? 8.5 : 12,
                lg: isChatOpen ? 8.5 : 12,
                xl: isChatOpen ? 8.5 : 12,
              }}
            >
              <Box>
                <Grid2 container>
                  <Grid2 size={{ xs: 12 }} className="pb-24">
                    <CustomCard
                      id="facility-patient-details"
                      className="width-100"
                    >
                      <CustomCardContent
                        id="facility-patient-details-content"
                        className="card-content"
                      >
                        <Box className="flex  align-items-center pb-24">
                          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                            <Box className="flex align-items-center">
                              <CustomTypography
                                id="patient-name"
                                variant="h5"
                                className="text-align-justify"
                              >
                                Consult Information
                              </CustomTypography>
                            </Box>
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                            <Box className="float-right flex align-items-center text-align-right">
                              <Box className="mr-12">
                                <CustomBadge
                                  id={`${opsConsultMoreDetails?.id}-chat-notification`}
                                  badgeContent={
                                    opsConsultMoreDetails?.unreadMessageCount
                                  }
                                  color="error"
                                  max={99}
                                >
                                  <CustomIconButton
                                    id="chat-icon"
                                    size="small"
                                    icon={<ChatIcon />}
                                    onClick={handleChatToggle}
                                  />
                                </CustomBadge>
                              </Box>
                              <Box>
                                <CustomButton
                                  id="save-button"
                                  onClick={() => {
                                    handleEditConsultInfo(
                                      opsConsultMoreDetails?.id || 0
                                    );
                                  }}
                                  variant="contained"
                                >
                                  Edit
                                </CustomButton>
                              </Box>
                            </Box>
                          </Grid2>
                        </Box>

                        <CustomDivider />
                        <Box className="pt-24 pb-12">
                          <Box>
                            <Grid2
                              container
                              spacing={0.5}
                              className="text-align-left"
                            >
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 4,
                                  xl: 4,
                                }}
                              >
                                <CustomTypography
                                  id="facility-name-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Facility Name
                                </CustomTypography>
                                <CustomTypography
                                  id="facility-name-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left"
                                >
                                  {getValueOrNA(
                                    opsConsultMoreDetails?.consultInformation
                                      ?.facilityName
                                  )}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 4,
                                  xl: 4,
                                }}
                              >
                                <CustomTypography
                                  id="partner-name-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Partner Name
                                </CustomTypography>
                                <CustomTypography
                                  id="partner-name-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left"
                                >
                                  {getValueOrNA(
                                    opsConsultMoreDetails?.consultInformation
                                      ?.partnerName
                                  )}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 4,
                                  xl: 4,
                                }}
                              >
                                <CustomTypography
                                  id="consult-status-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Consult Status
                                </CustomTypography>
                                <CustomTypography
                                  id="consult-status-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left"
                                >
                                  {getValueOrNA(
                                    opsConsultMoreDetails?.consultInformation
                                      ?.sessionStatus
                                  )}
                                </CustomTypography>
                              </Grid2>
                            </Grid2>
                          </Box>
                        </Box>
                      </CustomCardContent>
                    </CustomCard>
                  </Grid2>
                </Grid2>
                <Grid2 container>
                  <Grid2 size={{ xs: 12 }} className="pb-24">
                    <CustomCard
                      id="patient-information-details"
                      className="width-100"
                    >
                      <CustomCardContent
                        id="patient-information-details-content"
                        className="card-content"
                      >
                        <Box className="flex  align-items-center pb-24">
                          <Grid2
                            size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                          >
                            <Box className="flex align-items-center">
                              <CustomTypography
                                id="patient-information-title"
                                variant="h5"
                                className="text-align-justify"
                              >
                                Patient Information
                              </CustomTypography>
                            </Box>
                          </Grid2>
                        </Box>

                        <CustomDivider />
                        <Box className="pt-24">
                          <Box>
                            <Grid2
                              container
                              spacing={0.5}
                              className="text-align-left"
                            >
                              {opsConsultMoreDetails?.patientInformation.map(
                                (field, index) => (
                                  <Grid2
                                    key={`${field.fieldLabel}-${index}`}
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 4,
                                      lg: 2.4,
                                      xl: 2.4,
                                    }}
                                    className="text-left pb-12"
                                  >
                                    <Box>
                                      <CustomTypography
                                        id={`lbl-${field.fieldLabel}-${index}-label`}
                                        variant="body3"
                                        color="textSecondary"
                                        className="pb-4"
                                      >
                                        {field.fieldLabel}
                                      </CustomTypography>
                                      <CustomTypography
                                        id={`lbl-${field.fieldLabel}-${index}-value`}
                                        variant="body3"
                                      >
                                        {formatFieldValue(field.fieldValue, {
                                          isDateOfBirth:
                                            field.fieldLabel ===
                                            'Date Of Birth',
                                        })}
                                      </CustomTypography>
                                    </Box>
                                  </Grid2>
                                )
                              )}
                            </Grid2>
                          </Box>
                        </Box>
                      </CustomCardContent>
                    </CustomCard>
                  </Grid2>
                </Grid2>
                <Grid2 container>
                  <Grid2 size={{ xs: 12 }} className="pb-24">
                    <CustomCard
                      id="consult-additional-fields"
                      className="width-100"
                    >
                      <CustomCardContent
                        id="consult-additional-fields-content"
                        className="card-content"
                      >
                        <Box className="flex align-items-center pb-24">
                          <Grid2
                            size={{ xs: 12, sm: 12, md: 5.9, lg: 5.9, xl: 5.9 }}
                          >
                            <CustomTypography
                              id="additional-info-title"
                              variant="h5"
                              className="text-align-justify"
                            >
                              Additional Information
                            </CustomTypography>
                          </Grid2>
                        </Box>
                        <CustomDivider />
                        <Box className="pt-24">
                          <Box>
                            <Grid2
                              container
                              spacing={0.5}
                              className="text-align-left"
                            >
                              {opsConsultMoreDetails &&
                              opsConsultMoreDetails.additionalInformation
                                .length > 0 ? (
                                opsConsultMoreDetails.additionalInformation.map(
                                  (field, index) => (
                                    <Grid2
                                      key={`${field.fieldLabel}-${index}`}
                                      size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4,
                                        lg: 2.4,
                                        xl: 2.4,
                                      }}
                                      className="text-left pb-12"
                                    >
                                      <Box>
                                        <CustomTypography
                                          id={`lbl-${field.fieldLabel}-label`}
                                          variant="body3"
                                          color="textSecondary"
                                          className="pb-4"
                                        >
                                          {field.fieldLabel}
                                        </CustomTypography>
                                        {renderFieldValue(
                                          field.fieldLabel,
                                          field.fieldValue
                                        )}
                                      </Box>
                                    </Grid2>
                                  )
                                )
                              ) : (
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 2.4,
                                    xl: 2.4,
                                  }}
                                  className="button-width"
                                >
                                  <Box className="text-align-center no-consults-data">
                                    <CustomTypography
                                      variant="body3"
                                      color="textDisabled"
                                      id="noadditionalInfoAvailable"
                                    >
                                      No Additional Information Available
                                    </CustomTypography>
                                  </Box>
                                </Grid2>
                              )}
                            </Grid2>
                          </Box>
                        </Box>
                      </CustomCardContent>
                    </CustomCard>
                  </Grid2>
                </Grid2>
                <Grid2 container>
                  <Grid2 size={{ xs: 12 }} className="pb-24">
                    <CustomCard id="consult-details" className="width-100">
                      <CustomCardContent
                        id="consult-details-card"
                        className="card-content"
                      >
                        <Box className="flex align-items-center pb-24">
                          <Grid2
                            size={{ xs: 12, sm: 12, md: 5.9, lg: 5.9, xl: 5.9 }}
                          >
                            <CustomTypography
                              id="consult-details-title"
                              variant="h5"
                              className="text-align-justify"
                            >
                              Consult Details
                            </CustomTypography>
                          </Grid2>
                        </Box>
                        <CustomDivider />
                        <Box className="pt-24">
                          <Box>
                            <Grid2
                              container
                              spacing={0.5}
                              className="text-align-left"
                            >
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="wait-time-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Total Wait Time
                                </CustomTypography>
                                <CustomTypography
                                  id="wait-time-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getValueOrNA(
                                    opsConsultMoreDetails?.consultDetail
                                      .totalWaitTime
                                  )}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="wait-time-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Cal. Wait Time
                                </CustomTypography>
                                <CustomTypography
                                  id="wait-time-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getValueOrNA(
                                    opsConsultMoreDetails?.consultDetail
                                      .calculatedWaitTime
                                  )}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="hold-time-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Hold Time
                                </CustomTypography>
                                <CustomTypography
                                  id="hold-time-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getValueOrNA(
                                    opsConsultMoreDetails?.consultDetail
                                      .holdTime
                                  )}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="call-duration-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Call Duration
                                </CustomTypography>
                                <CustomTypography
                                  id="call-duration-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getValueOrNA(
                                    opsConsultMoreDetails?.consultDetail
                                      .callDuration
                                  )}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="created-time-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Created Time
                                </CustomTypography>
                                <CustomTypography
                                  id="created-time-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {formatFieldValue(
                                    opsConsultMoreDetails?.consultDetail
                                      .createdOn,
                                    {
                                      isDateOfBirth: false,
                                    }
                                  )}
                                </CustomTypography>
                              </Grid2>
                              {opsConsultMoreDetails?.consultInformation.sessionStatus.toLowerCase() ===
                                CallStatusEnum.Cancelled && (
                                <>
                                  <Grid2
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 4,
                                      lg: 2.4,
                                      xl: 2.4,
                                    }}
                                  >
                                    <CustomTypography
                                      id="cancel-reason-label"
                                      variant="body3"
                                      color="textDisabled"
                                      className="text-align-left pb-12"
                                    >
                                      Cancel Reason (
                                      {getFirstChar(
                                        opsConsultMoreDetails?.consultDetail
                                          .cancelPortalType
                                      )}
                                      )
                                    </CustomTypography>
                                    <CustomTypography
                                      id="cancel-reason-value"
                                      variant="body3"
                                      color="textPrimary"
                                      className="text-align-left pb-12"
                                    >
                                      {getValueOrNA(
                                        opsConsultMoreDetails?.consultDetail
                                          .cancelReason
                                      )}
                                    </CustomTypography>
                                  </Grid2>
                                  <Grid2
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 4,
                                      lg: 2.4,
                                      xl: 2.4,
                                    }}
                                  >
                                    <CustomTypography
                                      id="cancel-sub-reason-label"
                                      variant="body3"
                                      color="textDisabled"
                                      className="text-align-left pb-12"
                                    >
                                      Cancel Sub Reason (
                                      {getFirstChar(
                                        opsConsultMoreDetails?.consultDetail
                                          .cancelPortalType
                                      )}
                                      )
                                    </CustomTypography>
                                    <CustomTypography
                                      id="cancel-sub-reason-value"
                                      variant="body3"
                                      color="textPrimary"
                                      className="text-align-left pb-12"
                                    >
                                      {getValueOrNA(
                                        opsConsultMoreDetails?.consultDetail
                                          .cancelSubReason
                                      )}
                                    </CustomTypography>
                                  </Grid2>
                                  <Grid2
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 4,
                                      lg: 2.4,
                                      xl: 2.4,
                                    }}
                                  >
                                    <CustomTypography
                                      id="cancel-reason-other-label"
                                      variant="body3"
                                      color="textDisabled"
                                      className="text-align-left pb-12"
                                    >
                                      Cancel Reason Other (
                                      {getFirstChar(
                                        opsConsultMoreDetails?.consultDetail
                                          .cancelPortalType
                                      )}
                                      )
                                    </CustomTypography>
                                    <CustomTypography
                                      id="cancel-reason-other-value"
                                      variant="body3"
                                      color="textPrimary"
                                      className="text-align-left pb-12"
                                    >
                                      {getValueOrNA(
                                        opsConsultMoreDetails?.consultDetail
                                          .cancelOtherReason
                                      )}
                                    </CustomTypography>
                                  </Grid2>
                                </>
                              )}
                              {opsConsultMoreDetails?.consultInformation.sessionStatus.toLowerCase() ===
                                CallStatusEnum.OnHold && (
                                <>
                                  <Grid2
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 4,
                                      lg: 2.4,
                                      xl: 2.4,
                                    }}
                                  >
                                    <CustomTypography
                                      id="on-hold-reason-label"
                                      variant="body3"
                                      color="textDisabled"
                                      className="text-align-left pb-12"
                                    >
                                      On Hold Reason (
                                      {getFirstChar(
                                        opsConsultMoreDetails?.consultDetail
                                          .onHoldPortalType
                                      )}
                                      )
                                    </CustomTypography>
                                    <CustomTypography
                                      id="on-hold-reason-value"
                                      variant="body3"
                                      color="textPrimary"
                                      className="text-align-left pb-12"
                                    >
                                      {getValueOrNA(
                                        opsConsultMoreDetails?.consultDetail
                                          .onHoldReason
                                      )}
                                    </CustomTypography>
                                  </Grid2>
                                  <Grid2
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 4,
                                      lg: 2.4,
                                      xl: 2.4,
                                    }}
                                  >
                                    <CustomTypography
                                      id="on-hold-sub-reason-label"
                                      variant="body3"
                                      color="textDisabled"
                                      className="text-align-left pb-12"
                                    >
                                      On Hold Sub Reason (
                                      {getFirstChar(
                                        opsConsultMoreDetails?.consultDetail
                                          .onHoldPortalType
                                      )}
                                      )
                                    </CustomTypography>
                                    <CustomTypography
                                      id="on-hold-sub-reason-value"
                                      variant="body3"
                                      color="textPrimary"
                                      className="text-align-left"
                                    >
                                      {getValueOrNA(
                                        opsConsultMoreDetails?.consultDetail
                                          .onHoldSubReason
                                      )}
                                    </CustomTypography>
                                  </Grid2>
                                  <Grid2
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 4,
                                      lg: 2.4,
                                      xl: 2.4,
                                    }}
                                  >
                                    <CustomTypography
                                      id="on-hold-reason-other-label"
                                      variant="body3"
                                      color="textDisabled"
                                      className="text-align-left pb-12"
                                    >
                                      On Hold Reason Other (
                                      {getFirstChar(
                                        opsConsultMoreDetails?.consultDetail
                                          .onHoldPortalType
                                      )}
                                      )
                                    </CustomTypography>
                                    <CustomTypography
                                      id="on-hold-reason-other-value"
                                      variant="body3"
                                      color="textPrimary"
                                      className="text-align-left pb-12"
                                    >
                                      {getValueOrNA(
                                        opsConsultMoreDetails?.consultDetail
                                          .onHoldOtherReason
                                      )}
                                    </CustomTypography>
                                  </Grid2>
                                </>
                              )}
                            </Grid2>
                          </Box>
                        </Box>
                      </CustomCardContent>
                    </CustomCard>
                  </Grid2>
                </Grid2>
                {opsConsultMoreDetails?.consultInformation?.sessionStatus.toLowerCase() ===
                  CallStatusEnum.Completed && (
                  <Grid2 container>
                    <Grid2 size={{ xs: 12 }} className="pb-24">
                      <CustomCard
                        id="consult-observation"
                        className="width-100"
                      >
                        <CustomCardContent
                          id="consult-observation-card"
                          className="card-content"
                        >
                          <Box className="flex align-items-center pb-24">
                            <Grid2
                              size={{
                                xs: 12,
                                sm: 12,
                                md: 5.9,
                                lg: 5.9,
                                xl: 5.9,
                              }}
                            >
                              <CustomTypography
                                id="consult-observation-title"
                                variant="h5"
                                className="text-align-justify"
                              >
                                Consult Observation
                              </CustomTypography>
                            </Grid2>
                          </Box>
                          <CustomDivider />
                          <Box className="pt-24">
                            <Box>
                              <Grid2
                                container
                                spacing={0.5}
                                className="text-align-left"
                              >
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 2.4,
                                    xl: 2.4,
                                  }}
                                >
                                  <CustomTypography
                                    id="billing-consult-label"
                                    variant="body3"
                                    color="textDisabled"
                                    className="text-align-left pb-12"
                                  >
                                    Billing Consult?
                                  </CustomTypography>
                                  <CustomTypography
                                    id="billing-consult-value"
                                    variant="body3"
                                    color="textPrimary"
                                    className="text-align-left pb-12"
                                  >
                                    {getBoolenValue(
                                      opsConsultMoreDetails?.consultObservation
                                        ?.isBillableConsult
                                    )}
                                  </CustomTypography>
                                </Grid2>
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 2.4,
                                    xl: 2.4,
                                  }}
                                >
                                  <CustomTypography
                                    id="recomendations-label"
                                    variant="body3"
                                    color="textDisabled"
                                    className="text-align-left pb-12"
                                  >
                                    Recomendations
                                  </CustomTypography>
                                  <CustomTypography
                                    id="recomendations-value"
                                    variant="body3"
                                    color="textPrimary"
                                    className="text-align-left pb-12"
                                  >
                                    {getValueOrNA(
                                      opsConsultMoreDetails?.consultObservation
                                        ?.recommendation
                                    )}
                                  </CustomTypography>
                                </Grid2>
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 2.4,
                                    xl: 2.4,
                                  }}
                                >
                                  <CustomTypography
                                    id="diagnosis-label"
                                    variant="body3"
                                    color="textDisabled"
                                    className="text-align-left pb-12"
                                  >
                                    Diagnosis
                                  </CustomTypography>
                                  <CustomTypography
                                    id="diagnosis-value"
                                    variant="body3"
                                    color="textPrimary"
                                    className="text-align-left pb-12"
                                  >
                                    {getValueOrNA(
                                      opsConsultMoreDetails?.consultObservation
                                        ?.diagnosis
                                    )}
                                  </CustomTypography>
                                </Grid2>
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 2.4,
                                    xl: 2.4,
                                  }}
                                >
                                  <CustomTypography
                                    id="em-codes-label"
                                    variant="body3"
                                    color="textDisabled"
                                    className="text-align-left pb-12"
                                  >
                                    E/M Codes
                                  </CustomTypography>
                                  <CustomTypography
                                    id="em-codes-value"
                                    variant="body3"
                                    color="textPrimary"
                                    className="text-align-left pb-12"
                                  >
                                    {getValueOrNA(
                                      opsConsultMoreDetails?.consultObservation
                                        ?.emCodes
                                    )}
                                  </CustomTypography>
                                </Grid2>
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 2.4,
                                    xl: 2.4,
                                  }}
                                >
                                  <CustomTypography
                                    id="follow-up-label"
                                    variant="body3"
                                    color="textDisabled"
                                    className="text-align-left pb-12"
                                  >
                                    Follow-Up
                                  </CustomTypography>
                                  <CustomTypography
                                    id="follow-up-value"
                                    variant="body3"
                                    color="textPrimary"
                                    className="text-align-left pb-12"
                                  >
                                    {getValueOrNA(
                                      opsConsultMoreDetails?.consultObservation
                                        ?.followUp
                                    )}
                                  </CustomTypography>
                                </Grid2>
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 2.4,
                                    xl: 2.4,
                                  }}
                                >
                                  <CustomTypography
                                    id="surge-patient-label"
                                    variant="body3"
                                    color="textDisabled"
                                    className="text-align-left pb-12"
                                  >
                                    Surge Patient?
                                  </CustomTypography>
                                  <CustomTypography
                                    id="surge-patient-value"
                                    variant="body3"
                                    color="textPrimary"
                                    className="text-align-left pb-12"
                                  >
                                    {getBoolenValue(
                                      opsConsultMoreDetails?.consultObservation
                                        ?.isSurge
                                    )}
                                  </CustomTypography>
                                </Grid2>
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 2.4,
                                    xl: 2.4,
                                  }}
                                >
                                  <CustomTypography
                                    id="ops-user-comments-label"
                                    variant="body3"
                                    color="textDisabled"
                                    className="text-align-left pb-12"
                                  >
                                    Ops User Comments
                                  </CustomTypography>
                                  <CustomTypography
                                    id="ops-user-comments-value"
                                    variant="body3"
                                    color="textPrimary"
                                    className="text-align-left pb-12"
                                  >
                                    {getValueOrNA(
                                      opsConsultMoreDetails?.consultObservation
                                        ?.opsComments
                                    )}
                                  </CustomTypography>
                                </Grid2>
                              </Grid2>
                            </Box>
                          </Box>
                        </CustomCardContent>
                      </CustomCard>
                    </Grid2>
                  </Grid2>
                )}
                <Grid2 container>
                  <Grid2 size={{ xs: 12 }} className="pb-24">
                    <CustomCard id="consult-notes" className="width-100">
                      <CustomCardContent
                        id="consult-nores-card"
                        className="card-content"
                      >
                        <Box className="flex align-items-center pb-24">
                          <Grid2
                            size={{ xs: 12, sm: 12, md: 5.9, lg: 5.9, xl: 5.9 }}
                          >
                            <CustomTypography
                              id="consult-notes-title"
                              variant="h5"
                              className="text-align-justify"
                            >
                              Consult Notes
                            </CustomTypography>
                          </Grid2>
                        </Box>
                        <CustomDivider />
                        <Box className="pt-24">
                          <Grid2
                            size={{
                              xs: 12,
                              sm: 12,
                              md: 12,
                              lg: 12,
                              xl: 12,
                            }}
                          >
                            <CustomTypography
                              id="ops-consult-notes-label"
                              variant="body3"
                              color="textDisabled"
                              className="text-align-left pb-12"
                            >
                              Ops Consult Notes
                            </CustomTypography>
                            <CustomTypography
                              id="ops-consult-notes-value"
                              variant="body3"
                              color="textPrimary"
                              className="text-align-left pb-12"
                            >
                              {opsConsultMoreDetails?.notes || 'N/A'}
                            </CustomTypography>
                          </Grid2>
                        </Box>
                      </CustomCardContent>
                    </CustomCard>
                  </Grid2>
                </Grid2>
                <Grid2 container>
                  <Grid2 size={{ xs: 12 }} className="pb-24">
                    <CustomCard id="facility-details-tab" className="width-100">
                      <CustomCardContent
                        id="facility-details-tab-content"
                        className="card-content"
                      >
                        <Box>
                          <CustomTabs
                            id="facility-more-details-tab"
                            tabs={tabItems}
                            value={activeTab}
                            onChange={handleTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="scrollable"
                            allowScrollButtonsMobile={true}
                            orientation="horizontal"
                          />
                          <CustomDivider />
                        </Box>
                        <CustomDivider />
                        <Box>
                          <CustomTabPanel
                            value={activeTab}
                            index={0}
                            className="pb-none"
                          >
                            <Box>
                              <Grid2
                                container
                                spacing={0.5}
                                className="text-align-left"
                              >
                                {opsConsultMoreDetails &&
                                opsConsultMoreDetails.facilityDetails.length >
                                  0 ? (
                                  opsConsultMoreDetails.facilityDetails.map(
                                    (field, index) => {
                                      return (
                                        <Grid2
                                          key={`${field.fieldLabel}-${index}`}
                                          size={{
                                            xs: 12,
                                            sm: 12,
                                            md: 4,
                                            lg: 2.4,
                                            xl: 2.4,
                                          }}
                                          className="text-left pb-24"
                                        >
                                          <Box>
                                            <CustomTypography
                                              id={`lbl-${field.fieldLabel}-label`}
                                              variant="body3"
                                              color="textSecondary"
                                              className="pb-4"
                                            >
                                              {field.fieldLabel}
                                            </CustomTypography>
                                            {renderFieldValue(
                                              field.fieldLabel,
                                              field.fieldValue
                                            )}
                                          </Box>
                                        </Grid2>
                                      );
                                    }
                                  )
                                ) : (
                                  <Grid2
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 4,
                                      lg: 2.4,
                                      xl: 2.4,
                                    }}
                                    className="button-width"
                                  >
                                    <Box className="text-align-center no-consults-data">
                                      <CustomTypography
                                        variant="body3"
                                        color="textDisabled"
                                        id="no-details-available-text"
                                      >
                                        No Facility Fields Available
                                      </CustomTypography>
                                    </Box>
                                  </Grid2>
                                )}
                              </Grid2>
                            </Box>
                          </CustomTabPanel>
                          <CustomTabPanel value={activeTab} index={1}>
                            <CustomTypography
                              id="past-sessions-tab-content"
                              variant="body3"
                            >
                              <Box>
                                <Grid2 container>
                                  <Grid2
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 12,
                                      lg: 12,
                                      xl: 12,
                                    }}
                                    className="text-left"
                                  >
                                    <PastSessions
                                      consultId={Number(consultId)}
                                    />
                                  </Grid2>
                                </Grid2>
                              </Box>
                            </CustomTypography>
                          </CustomTabPanel>

                          <CustomTabPanel value={activeTab} index={2}>
                            <Box>
                              <Grid2 container>
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12,
                                  }}
                                  className="text-align-right"
                                >
                                  <CustomButton
                                    id="btn-upload-doc"
                                    variant="outlined"
                                    color="success"
                                    onClick={handleFileUploadOpen}
                                  >
                                    Upload
                                  </CustomButton>
                                  <CustomDialog
                                    id="dialogWarning"
                                    open={openFileUpload}
                                    onClose={handleFileUploadClose}
                                  >
                                    <Box className="p-16">
                                      <CustomDialogContent>
                                        <CustomDragZone
                                          getFiles={uploadDocument}
                                          uploadBtnRequired
                                          onClose={handleFileUploadClose}
                                          isUploading={isPending}
                                        />
                                        <Box className="pt-12">
                                          {errorMessage && (
                                            <CustomTypography
                                              id="upload-documents-error"
                                              color="error"
                                              variant="body3"
                                            >
                                              {errorMessage}
                                            </CustomTypography>
                                          )}
                                        </Box>
                                      </CustomDialogContent>
                                    </Box>
                                  </CustomDialog>
                                </Grid2>
                              </Grid2>
                            </Box>

                            <Box>
                              <Grid2 container>
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12,
                                  }}
                                  className="text-left"
                                >
                                  <SessionDoc consultId={Number(consultId)} />
                                </Grid2>
                              </Grid2>
                            </Box>
                          </CustomTabPanel>

                          <CustomTabPanel value={activeTab} index={3}>
                            <CustomTypography
                              id="history-tab-content"
                              variant="body3"
                            >
                              <Box>
                                <Grid2 container>
                                  <Grid2
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 12,
                                      lg: 12,
                                      xl: 12,
                                    }}
                                    className="text-left"
                                  >
                                    {consultHistory && (
                                      <ConsultHistory
                                        consultHistory={consultHistory}
                                        isLoading={historyLoading}
                                      />
                                    )}
                                    {!consultHistory && !historyLoading && (
                                      <Box>No consultation history found</Box>
                                    )}
                                    {historyLoading && <Box>Loading...</Box>}
                                  </Grid2>
                                </Grid2>
                              </Box>
                            </CustomTypography>
                          </CustomTabPanel>
                        </Box>
                      </CustomCardContent>
                    </CustomCard>
                  </Grid2>
                </Grid2>
              </Box>
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 12,
                md: isChatOpen ? 3.5 : 0,
                lg: isChatOpen ? 3.5 : 0,
                xl: isChatOpen ? 3.5 : 0,
              }}
            >
              <Box className="pb-24">
                <ChatQLER
                  onClose={() => setIsChatOpen(false)}
                  acsConfig={getAcsConfig()}
                  isViewOnly={setViewOnly(user?.role)}
                  threadId={consultChatThread?.threadId}
                  subtitle={consultChatThread?.participants || ''}
                  hidden={!isChatOpen}
                />
              </Box>
            </Grid2>
          </Grid2>
        </Box>
      </Box>
    </Box>
  );
};

export default ConsultMoreDetailsOps;
